import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Plus, Trash2, ToggleLeft, ToggleRight, Edit, X, Copy, Mail, Phone,
  ExternalLink, MessageCircle, DollarSign, RefreshCw, Share2, Send
} from "lucide-react";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const EMPTY_FORM = {
  code: "",
  name: "",
  email: "",
  phone: "",
  notes: "",
  credit_amount: 20,
  is_active: true,
};

const AdminMarketCampAmbassadors = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/market-camp/ambassadors`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Failed to load ambassadors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const sendKitEmail = async (a) => {
    if (!a.email) {
      toast.error("This ambassador has no email on file. Edit them to add one.");
      return;
    }
    if (!window.confirm(`Send share-kit email to ${a.name} (${a.email})?\n\nThey'll receive their code ${a.code}, share link, and payout instructions.`)) return;
    try {
      await axios.post(`${API}/admin/market-camp/ambassadors/${a.id}/send-email`);
      toast.success(`Share kit emailed to ${a.email}`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send share kit");
    }
  };

  const startCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      code: a.code,
      name: a.name || "",
      email: a.email || "",
      phone: a.phone || "",
      notes: a.notes || "",
      credit_amount: a.credit_amount ?? 20,
      is_active: a.is_active !== false,
    });
    setShowForm(true);
  };

  const generateRandomCode = () => {
    const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let body = "";
    for (let i = 0; i < 6; i += 1) body += alphabet[Math.floor(Math.random() * alphabet.length)];
    setForm(f => ({ ...f, code: body }));
  };

  const saveAmbassador = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.credit_amount === "" || form.credit_amount === null || Number(form.credit_amount) < 0) {
      toast.error("Credit amount must be 0 or greater");
      return;
    }
    const payload = {
      ...form,
      code: (form.code || "").trim().toUpperCase(),
      credit_amount: parseFloat(form.credit_amount),
    };
    try {
      if (editing) {
        const { code: _c, ...rest } = payload; // code immutable
        await axios.patch(`${API}/admin/market-camp/ambassadors/${editing}`, rest);
        toast.success("Ambassador updated");
      } else {
        await axios.post(`${API}/admin/market-camp/ambassadors`, payload);
        toast.success("Ambassador created");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ ...EMPTY_FORM });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(`${API}/admin/market-camp/ambassadors/${a.id}`, { is_active: !a.is_active });
      fetchData();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const deleteAmbassador = async (id) => {
    if (!window.confirm("Delete this ambassador? Existing earned credits are NOT deleted.")) return;
    try {
      await axios.delete(`${API}/admin/market-camp/ambassadors/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const copyText = (text, label = "Copied") => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(label);
    } catch (e) {
      toast.error("Could not copy");
    }
  };

  const shareLink = (code) => `${FRONTEND_URL}/mini-makers-market-camp?ref=${code}`;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 text-[#F5F5F0]" data-testid="admin-market-camp-ambassadors-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2">
              <Users className="w-7 h-7 text-[#D4AF37]" />
              Market Camp · Ambassadors
            </h1>
            <p className="text-[#A1A1AA] text-sm mt-1">
              Create standalone referral codes for partners, schools, influencers, or anyone who wants
              to promote the camp without registering. Each ambassador earns a custom credit (e.g. $10, $20, $50)
              per successful registration through their code.
            </p>
          </div>
          <button
            onClick={startCreate}
            className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#050505] font-bold inline-flex items-center gap-2 hover:bg-[#B8943B]"
            data-testid="new-ambassador-btn"
          >
            <Plus className="w-4 h-4" /> New Ambassador
          </button>
        </div>

        <div className="bg-[#0A0A0A] rounded-2xl border border-[rgba(212,175,55,0.15)] overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-[#A1A1AA]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">No ambassadors yet.</p>
              <button
                onClick={startCreate}
                className="mt-4 px-4 py-2 rounded-lg bg-[#D4AF37] text-[#050505] font-bold inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Create your first ambassador
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#A1A1AA] border-b border-[rgba(212,175,55,0.15)]">
                    <th className="text-left px-3 py-3">Ambassador</th>
                    <th className="text-left px-3 py-3">Code</th>
                    <th className="text-left px-3 py-3">Per Referral</th>
                    <th className="text-left px-3 py-3">Performance</th>
                    <th className="text-left px-3 py-3">Status</th>
                    <th className="text-left px-3 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} className="border-b border-[rgba(212,175,55,0.08)] hover:bg-[rgba(212,175,55,0.04)]" data-testid={`amb-row-${a.id}`}>
                      <td className="px-3 py-3 align-top max-w-[220px]">
                        <p className="text-[#F5F5F0] font-bold">{a.name}</p>
                        {a.email && <p className="text-[11px] text-[#A1A1AA] flex items-center gap-1 mt-0.5 truncate"><Mail className="w-3 h-3" /> {a.email}</p>}
                        {a.phone && <p className="text-[11px] text-[#A1A1AA] flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {a.phone}</p>}
                        {a.notes && <p className="text-[10px] text-[#52525B] italic mt-1 truncate" title={a.notes}>{a.notes}</p>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="font-mono font-black text-[#D4AF37] text-base tracking-[2px]">{a.code}</p>
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => copyText(a.code, "Code copied")} className="text-[10px] text-[#A1A1AA] hover:text-[#D4AF37] inline-flex items-center gap-0.5" title="Copy code">
                            <Copy className="w-2.5 h-2.5" /> code
                          </button>
                          <span className="text-[10px] text-[#3F3F46]">|</span>
                          <button onClick={() => copyText(shareLink(a.code), "Share link copied")} className="text-[10px] text-[#A1A1AA] hover:text-[#D4AF37] inline-flex items-center gap-0.5" title="Copy share link">
                            <Share2 className="w-2.5 h-2.5" /> link
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="font-bold text-[#F5F5F0] text-base">${a.credit_amount?.toFixed(2)}</p>
                        <p className="text-[10px] text-[#A1A1AA]">per signup</p>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="text-[#F5F5F0] font-bold">{a.redemptions || 0} <span className="text-[10px] font-normal text-[#A1A1AA]">refs</span></p>
                        {a.pending_amount > 0 && (
                          <p className="text-[10px] text-[#D4AF37]">${a.pending_amount?.toFixed(2)} pending</p>
                        )}
                        {a.paid_amount > 0 && (
                          <p className="text-[10px] text-green-400">${a.paid_amount?.toFixed(2)} paid</p>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        {a.is_active
                          ? <Pill bg="rgba(63,164,55,0.15)" color="#3FA437" label="Active" />
                          : <Pill bg="rgba(161,161,170,0.15)" color="#A1A1AA" label="Inactive" />}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="flex gap-1.5">
                          <a
                            href={shareLink(a.code)}
                            target="_blank" rel="noopener noreferrer"
                            className="p-1.5 rounded bg-[#27272A] hover:bg-[#3F3F46] text-[#A1A1AA]"
                            title="Open public landing with this code"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          {a.email && (
                            <button
                              onClick={() => sendKitEmail(a)}
                              className="p-1.5 rounded bg-blue-500/15 hover:bg-blue-500/25 text-blue-400"
                              title="Email this ambassador their share kit"
                              data-testid={`send-kit-${a.id}`}
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          {a.phone && (
                            <a
                              href={`https://wa.me/${(a.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Hi ${a.name?.split(" ")[0] || "there"}, here's your Mini Makers Market Camp ambassador code: ${a.code}. You earn $${a.credit_amount} per family who registers through your code. Share link: ${shareLink(a.code)}`)}`}
                              target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366]"
                              title="WhatsApp this ambassador the code"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                          )}
                          <button onClick={() => toggleActive(a)} className="p-1.5 rounded bg-[#27272A] hover:bg-[#3F3F46]" title={a.is_active ? "Deactivate" : "Activate"}>
                            {a.is_active ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-[#A1A1AA]" />}
                          </button>
                          <button onClick={() => startEdit(a)} className="p-1.5 rounded bg-[#27272A] hover:bg-[#3F3F46] text-[#A1A1AA]" title="Edit" data-testid={`edit-${a.id}`}>
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteAmbassador(a.id)} className="p-1.5 rounded bg-red-500/15 hover:bg-red-500/25 text-red-400" title="Delete" data-testid={`delete-${a.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-[#F5F5F0]">
                  {editing ? "Edit Ambassador" : "New Ambassador"}
                </h3>
                <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <FormField label="Name / Business" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Acme Dance Studio, Sarah Adebayo"
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    data-testid="amb-name"
                  />
                </FormField>
                <FormField label="Code (blank = auto-generate)">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.code}
                      onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                      placeholder="6 chars · blank to auto"
                      disabled={!!editing}
                      maxLength={20}
                      className="flex-1 px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] font-mono font-bold tracking-[2px] focus:border-[#D4AF37] outline-none text-sm uppercase disabled:opacity-50"
                      data-testid="amb-code"
                    />
                    {!editing && (
                      <button
                        type="button"
                        onClick={generateRandomCode}
                        className="px-3 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] font-bold text-xs inline-flex items-center gap-1"
                        data-testid="amb-gen-code"
                        title="Generate random code"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Gen
                      </button>
                    )}
                  </div>
                </FormField>
                <FormField label="Credit Amount per Referral ($)" required>
                  <div className="relative">
                    <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                    <input
                      type="number"
                      min="0" step="0.01"
                      value={form.credit_amount}
                      onChange={(e) => setForm(f => ({ ...f, credit_amount: e.target.value }))}
                      className="w-full pl-8 pr-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm font-bold"
                      data-testid="amb-credit-amount"
                    />
                  </div>
                  <p className="text-[10px] text-[#52525B] mt-1">Common: $10, $20, $30, $50. Each ambassador can earn a different amount.</p>
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Email">
                    <input
                      type="email" value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="optional"
                      className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    />
                  </FormField>
                  <FormField label="Phone / WhatsApp">
                    <input
                      type="tel" value={form.phone}
                      onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="+1780..."
                      className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    />
                  </FormField>
                </div>
                <FormField label="Notes (admin only)">
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Local dance studio partner · negotiated higher payout · met at conference"
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm resize-none"
                  />
                </FormField>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox" checked={form.is_active}
                    onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                    className="w-4 h-4 rounded" style={{ accentColor: "#D4AF37" }}
                  />
                  <span className="text-sm text-[#F5F5F0]">Active (code can be redeemed)</span>
                </label>
              </div>
              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#27272A] text-[#F5F5F0] font-bold hover:bg-[#3F3F46]"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAmbassador}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#D4AF37] text-[#050505] font-bold"
                  data-testid="save-amb"
                >
                  {editing ? "Save changes" : "Create Ambassador"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const Pill = ({ bg, color, label }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider" style={{ background: bg, color }}>
    {label}
  </span>
);

const FormField = ({ label, required, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold mb-1.5">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
  </div>
);

export default AdminMarketCampAmbassadors;
